import React, { useState, Component } from 'react';
import { Shield, AlertTriangle, CheckCircle, Clock, FileText, Download, ExternalLink, TrendingUp, AlertCircle } from 'lucide-react';
// --- Mock Data ---
const complianceStatus = {
  overall: 'good',
  score: 87,
  lastAudit: '2024-11-15',
  nextAudit: '2025-02-15'
};
const regulatoryRequirements = [{
  id: 1,
  name: 'FCA RMAR Quarterly Return',
  regulator: 'FCA',
  frequency: 'Quarterly',
  lastFiled: '2024-10-31',
  nextDue: '2025-01-31',
  status: 'current',
  daysUntil: 42
}, {
  id: 2,
  name: 'Solvency II Annual Return',
  regulator: 'PRA',
  frequency: 'Annual',
  lastFiled: '2024-01-15',
  nextDue: '2025-01-15',
  status: 'upcoming',
  daysUntil: 26
}, {
  id: 3,
  name: "Lloyd's Syndicate Reporting",
  regulator: "Lloyd's",
  frequency: 'Quarterly',
  lastFiled: '2024-11-20',
  nextDue: '2024-12-20',
  status: 'urgent',
  daysUntil: 1
}, {
  id: 4,
  name: 'GDPR Data Protection Assessment',
  regulator: 'ICO',
  frequency: 'Annual',
  lastFiled: '2024-03-01',
  nextDue: '2025-03-01',
  status: 'current',
  daysUntil: 71
}, {
  id: 5,
  name: 'AML/CTF Annual Report',
  regulator: 'FCA',
  frequency: 'Annual',
  lastFiled: '2024-04-30',
  nextDue: '2025-04-30',
  status: 'current',
  daysUntil: 131
}, {
  id: 6,
  name: 'ORSA Report',
  regulator: 'PRA',
  frequency: 'Annual',
  lastFiled: '2024-06-30',
  nextDue: '2025-06-30',
  status: 'current',
  daysUntil: 192
}];
const complianceChecklist = [{
  category: 'Regulatory Filings',
  items: [{
    task: 'Q4 RMAR Return',
    status: 'complete',
    dueDate: '2024-10-31'
  }, {
    task: "Lloyd's Quarterly Report",
    status: 'in-progress',
    dueDate: '2024-12-20'
  }, {
    task: 'Solvency II QRT',
    status: 'pending',
    dueDate: '2025-01-15'
  }]
}, {
  category: 'Internal Policies',
  items: [{
    task: 'Underwriting Policy Review',
    status: 'complete',
    dueDate: '2024-11-30'
  }, {
    task: 'Risk Management Framework Update',
    status: 'in-progress',
    dueDate: '2024-12-31'
  }, {
    task: 'Claims Handling Procedures',
    status: 'complete',
    dueDate: '2024-10-15'
  }]
}, {
  category: 'Audits & Reviews',
  items: [{
    task: 'Internal Audit - Claims',
    status: 'pending',
    dueDate: '2025-01-22'
  }, {
    task: 'External Audit Planning',
    status: 'in-progress',
    dueDate: '2024-12-15'
  }, {
    task: 'Compliance Monitoring Report',
    status: 'complete',
    dueDate: '2024-11-30'
  }]
}, {
  category: 'Training & Certification',
  items: [{
    task: 'AML Training - All Staff',
    status: 'complete',
    dueDate: '2024-09-30'
  }, {
    task: 'GDPR Refresher Course',
    status: 'in-progress',
    dueDate: '2024-12-31'
  }, {
    task: 'Senior Manager Certification',
    status: 'complete',
    dueDate: '2024-08-15'
  }]
}];
const auditSchedule = [{
  id: 1,
  type: 'Internal Audit',
  focus: 'Claims Process',
  date: '2025-01-22',
  status: 'scheduled',
  auditor: 'Internal Audit Team'
}, {
  id: 2,
  type: 'External Audit',
  focus: 'Financial Statements',
  date: '2025-02-15',
  status: 'scheduled',
  auditor: 'KPMG'
}, {
  id: 3,
  type: 'Regulatory Review',
  focus: 'Underwriting Standards',
  date: '2025-03-10',
  status: 'scheduled',
  auditor: 'FCA'
}, {
  id: 4,
  type: 'Internal Audit',
  focus: 'IT Security & Data Protection',
  date: '2025-04-05',
  status: 'scheduled',
  auditor: 'Internal Audit Team'
}];
const outstandingActions = [{
  id: 1,
  action: 'Update Reinsurance Security Policy',
  owner: 'Risk Manager',
  priority: 'high',
  dueDate: '2024-12-31',
  progress: 60
}, {
  id: 2,
  action: 'Implement Enhanced Due Diligence Procedures',
  owner: 'Compliance Officer',
  priority: 'critical',
  dueDate: '2024-12-20',
  progress: 85
}, {
  id: 3,
  action: 'Complete Cyber Security Assessment',
  owner: 'IT Manager',
  priority: 'high',
  dueDate: '2025-01-15',
  progress: 40
}, {
  id: 4,
  action: 'Review and Update Business Continuity Plan',
  owner: 'Operations Director',
  priority: 'medium',
  dueDate: '2025-01-31',
  progress: 25
}];
// --- Helper Functions ---
const getStatusColor = (status: string) => {
  switch (status) {
    case 'current':
      return 'bg-green-50 text-green-700 border-green-200';
    case 'upcoming':
      return 'bg-purple-50 text-purple-700 border-purple-200';
    case 'urgent':
      return 'bg-pink-50 text-pink-700 border-pink-200';
    default:
      return 'bg-neutral-100 text-neutral-700 border-neutral-200';
  }
};
const getStatusIcon = (status: string) => {
  switch (status) {
    case 'complete':
      return <CheckCircle size={16} className="text-green-600" data-id="element-1270" />;
    case 'in-progress':
      return <Clock size={16} className="text-purple-600" data-id="element-1271" />;
    case 'pending':
      return <AlertCircle size={16} className="text-neutral-400" data-id="element-1272" />;
    default:
      return null;
  }
};
const getPriorityColor = (priority: string) => {
  switch (priority) {
    case 'critical':
      return 'bg-pink-50 text-pink-700';
    case 'high':
      return 'bg-purple-50 text-purple-700';
    case 'medium':
      return 'bg-neutral-100 text-neutral-700';
    case 'low':
      return 'bg-neutral-50 text-neutral-600';
    default:
      return 'bg-neutral-100 text-neutral-700';
  }
};
const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};
// --- Components ---
export function Compliance() {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const urgentCount = regulatoryRequirements.filter(r => r.status === 'urgent' || r.daysUntil <= 7).length;
  const upcomingCount = regulatoryRequirements.filter(r => r.status === 'upcoming' && r.daysUntil > 7 && r.daysUntil <= 30).length;
  const totalTasks = complianceChecklist.reduce((sum, cat) => sum + cat.items.length, 0);
  const completedTasks = complianceChecklist.reduce((sum, cat) => sum + cat.items.filter(i => i.status === 'complete').length, 0);
  const completionRate = Math.round(completedTasks / totalTasks * 100);
  return <div className="space-y-6 animate-in fade-in duration-500" data-id="element-1273">
      {/* Header */}
      <div className="flex justify-between items-center" data-id="element-1274">
        <div data-id="element-1275">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-1276">
            Compliance Dashboard
          </h1>
          <p className="text-gray-500 mt-1" data-id="element-1277">
            Regulatory oversight and compliance tracking • FY 2024
          </p>
        </div>
        <div className="flex items-center gap-4" data-id="element-1278">
          <div className="flex items-center gap-2 px-3 py-1 bg-green-50 rounded-full border border-green-200" data-id="element-1279">
            <Shield className="text-green-600" size={16} data-id="element-1280" />
            <span className="text-sm font-medium text-green-700" data-id="element-1281">
              {complianceStatus.score}% Compliant
            </span>
          </div>
          <button className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors shadow-sm" data-id="element-1282">
            <Download size={16} className="mr-2" data-id="element-1283" />
            Export Report
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-1284">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-1285">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1" data-id="element-1286">
            Compliance Score
          </p>
          <p className="text-2xl font-bold text-gray-900" data-id="element-1287">
            {complianceStatus.score}%
          </p>
          <p className="text-xs text-green-600 mt-1" data-id="element-1288">Good standing</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-pink-200 p-4 bg-pink-50" data-id="element-1289">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1" data-id="element-1290">
            Urgent Items
          </p>
          <p className="text-2xl font-bold text-pink-600" data-id="element-1291">{urgentCount}</p>
          <p className="text-xs text-pink-700 mt-1" data-id="element-1292">Due within 7 days</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-1293">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1" data-id="element-1294">
            Upcoming
          </p>
          <p className="text-2xl font-bold text-gray-900" data-id="element-1295">{upcomingCount}</p>
          <p className="text-xs text-gray-600 mt-1" data-id="element-1296">Due within 30 days</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-1297">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1" data-id="element-1298">
            Task Completion
          </p>
          <p className="text-2xl font-bold text-gray-900" data-id="element-1299">{completionRate}%</p>
          <p className="text-xs text-gray-600 mt-1" data-id="element-1300">
            {completedTasks} of {totalTasks} complete
          </p>
        </div>
      </div>

      {/* Regulatory Requirements */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1301">
        <div className="flex justify-between items-center mb-6" data-id="element-1302">
          <h2 className="text-lg font-semibold text-gray-900" data-id="element-1303">
            Regulatory Requirements & Deadlines
          </h2>
          <div className="text-sm text-gray-500" data-id="element-1304">
            Next Audit:{' '}
            <span className="font-semibold text-gray-900" data-id="element-1305">
              {formatDate(complianceStatus.nextAudit)}
            </span>
          </div>
        </div>

        <div className="overflow-x-auto" data-id="element-1306">
          <table className="w-full" data-id="element-1307">
            <thead data-id="element-1308">
              <tr className="border-b-2 border-gray-200" data-id="element-1309">
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1310">
                  Requirement
                </th>
                <th className="text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1311">
                  Regulator
                </th>
                <th className="text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1312">
                  Frequency
                </th>
                <th className="text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1313">
                  Last Filed
                </th>
                <th className="text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1314">
                  Next Due
                </th>
                <th className="text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1315">
                  Days Until
                </th>
                <th className="text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1316">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100" data-id="element-1317">
              {regulatoryRequirements.map(req => <tr key={req.id} className="hover:bg-gray-50 transition-colors" data-id="element-1318">
                  <td className="py-3 px-4 text-sm font-medium text-gray-900" data-id="element-1319">
                    {req.name}
                  </td>
                  <td className="py-3 px-4 text-sm text-center text-gray-600" data-id="element-1320">
                    {req.regulator}
                  </td>
                  <td className="py-3 px-4 text-sm text-center text-gray-600" data-id="element-1321">
                    {req.frequency}
                  </td>
                  <td className="py-3 px-4 text-sm text-center text-gray-600" data-id="element-1322">
                    {formatDate(req.lastFiled)}
                  </td>
                  <td className="py-3 px-4 text-sm text-center font-medium text-gray-900" data-id="element-1323">
                    {formatDate(req.nextDue)}
                  </td>
                  <td className="py-3 px-4 text-sm text-center" data-id="element-1324">
                    <span className={`font-semibold ${req.daysUntil <= 7 ? 'text-pink-600' : req.daysUntil <= 30 ? 'text-purple-600' : 'text-gray-600'}`} data-id="element-1325">
                      {req.daysUntil}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-1326">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(req.status)}`} data-id="element-1327">
                      {req.status === 'urgent' ? 'Urgent' : req.status === 'upcoming' ? 'Upcoming' : 'Current'}
                    </span>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1328">
        {/* Compliance Checklist */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1329">
          <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1330">
            Compliance Checklist
          </h2>
          <div className="space-y-4" data-id="element-1331">
            {complianceChecklist.map((category, idx) => <div key={idx} className="border border-gray-200 rounded-lg" data-id="element-1332">
                <button onClick={() => setSelectedCategory(selectedCategory === category.category ? null : category.category)} className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors" data-id="element-1333">
                  <div className="flex items-center gap-3" data-id="element-1334">
                    <FileText size={18} className="text-gray-400" data-id="element-1335" />
                    <span className="font-medium text-gray-900" data-id="element-1336">
                      {category.category}
                    </span>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-1337">
                    <span className="text-sm text-gray-500" data-id="element-1338">
                      {category.items.filter(i => i.status === 'complete').length}
                      /{category.items.length}
                    </span>
                    <CheckCircle size={16} className={category.items.every(i => i.status === 'complete') ? 'text-green-600' : 'text-gray-300'} data-id="element-1339" />
                  </div>
                </button>
                {selectedCategory === category.category && <div className="border-t border-gray-200 p-4 space-y-2" data-id="element-1340">
                    {category.items.map((item, itemIdx) => <div key={itemIdx} className="flex items-center justify-between py-2" data-id="element-1341">
                        <div className="flex items-center gap-2" data-id="element-1342">
                          {getStatusIcon(item.status)}
                          <span className="text-sm text-gray-700" data-id="element-1343">
                            {item.task}
                          </span>
                        </div>
                        <span className="text-xs text-gray-500" data-id="element-1344">
                          {formatDate(item.dueDate)}
                        </span>
                      </div>)}
                  </div>}
              </div>)}
          </div>
        </div>

        {/* Audit Schedule */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1345">
          <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1346">
            Audit Schedule
          </h2>
          <div className="space-y-3" data-id="element-1347">
            {auditSchedule.map(audit => <div key={audit.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow" data-id="element-1348">
                <div className="flex items-start justify-between mb-2" data-id="element-1349">
                  <div data-id="element-1350">
                    <h3 className="text-sm font-semibold text-gray-900" data-id="element-1351">
                      {audit.type}
                    </h3>
                    <p className="text-xs text-gray-600 mt-1" data-id="element-1352">{audit.focus}</p>
                  </div>
                  <span className="text-xs px-2 py-1 rounded-full bg-purple-100 text-purple-700" data-id="element-1353">
                    {audit.status}
                  </span>
                </div>
                <div className="flex items-center justify-between mt-3 text-xs text-gray-500" data-id="element-1354">
                  <span className="flex items-center gap-1" data-id="element-1355">
                    <Clock size={12} data-id="element-1356" />
                    {formatDate(audit.date)}
                  </span>
                  <span data-id="element-1357">{audit.auditor}</span>
                </div>
              </div>)}
          </div>
        </div>
      </div>

      {/* Outstanding Actions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1358">
        <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1359">
          Outstanding Actions
        </h2>
        <div className="space-y-3" data-id="element-1360">
          {outstandingActions.map(action => <div key={action.id} className="border border-gray-200 rounded-lg p-4" data-id="element-1361">
              <div className="flex items-start justify-between mb-3" data-id="element-1362">
                <div className="flex-1" data-id="element-1363">
                  <h3 className="text-sm font-semibold text-gray-900" data-id="element-1364">
                    {action.action}
                  </h3>
                  <p className="text-xs text-gray-600 mt-1" data-id="element-1365">
                    Owner: {action.owner} • Due: {formatDate(action.dueDate)}
                  </p>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full font-medium ${getPriorityColor(action.priority)}`} data-id="element-1366">
                  {action.priority}
                </span>
              </div>
              <div className="space-y-1" data-id="element-1367">
                <div className="flex items-center justify-between text-xs text-gray-600" data-id="element-1368">
                  <span data-id="element-1369">Progress</span>
                  <span className="font-medium" data-id="element-1370">{action.progress}%</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-1371">
                  <div className="h-full bg-blue-600 rounded-full transition-all duration-500" style={{
                width: `${action.progress}%`
              }} data-id="element-1372" />
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </div>;
}